import React, { useState, useEffect, useRef } from "react";
import { navigate } from "@reach/router";
import { AuthConsumer } from "../helpers/AuthContext";
import GoogleSignInButton from "../components/GoogleSignInButton";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Set when sign-in is blocked because the email isn't verified yet —
  // drives the "Verify your email" prompt below.
  const [unverifiedEmail, setUnverifiedEmail] = useState("");
  const redirectedRef = useRef(false);

  // Arrived via the global 401 interceptor (index.js) after the stored
  // token expired — tell the user why they were signed out.
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("expired")) {
      setMessage("Your session expired. Please sign in again.");
    }
  }, []);

  // If already logged in, bounce to dashboard — exactly once, via a ref guard
  // so the effect can't stack timers across re-renders.
  useEffect(() => {
    if (redirectedRef.current) return;
    const storedEmail = localStorage.getItem("Email");
    if (!storedEmail || storedEmail === "guest") return;
    redirectedRef.current = true;
    const t = setTimeout(() => navigate("/dashboard"), 900);
    return () => clearTimeout(t);
  }, []);

  const handleSubmit = (login) => {
    if (!email || !password) {
      setMessage("Enter your email and password.");
      return;
    }
    setIsSubmitting(true);
    setMessage("");
    setUnverifiedEmail("");
    login({ email, password })
      .then(() => {
        setIsSubmitting(false);
        navigate("/dashboard");
      })
      .catch((err) => {
        setIsSubmitting(false);
        const data = err?.response?.data;
        // Account exists but the email isn't verified — offer the verify path.
        if (data?.emailVerificationRequired) {
          setUnverifiedEmail(data.email || email);
        }
        const msg = data?.message || "Authentication failed. Try again.";
        setMessage(msg);
      });
  };

  // Sign in with a Google credential. New Google users (incomplete
  // profile) go to the profile form; everyone else to the dashboard.
  const handleGoogle = (googleLogin, credential) => {
    setMessage("");
    setUnverifiedEmail("");
    setIsSubmitting(true);
    googleLogin(credential)
      .then((data) => {
        setIsSubmitting(false);
        navigate(data && data.profileComplete === false ? "/complete-profile" : "/dashboard");
      })
      .catch((err) => {
        setIsSubmitting(false);
        setMessage(
          err?.response?.data?.message || "Google sign-in failed. Please try again."
        );
      });
  };

  return (
    <AuthConsumer>
      {({ Auth, Name, login, googleLogin }) => {
        const isLoggedIn = Auth === "Admin" || Auth === "User";

        if (isLoggedIn) {
          return (
            <div className="theme-scientific auth-page">
              <div className="auth-shell auth-shell--solo">
                <div className="auth-form-panel">
                  <div className="sci-eyebrow">ICMOBI / Session active</div>
                  <h1 className="auth-form-panel__title">
                    Welcome back, <em style={{ fontStyle: "italic" }}>{Name}</em>
                  </h1>
                  <p className="auth-form-panel__subtitle">
                    You are already authenticated. Redirecting to your dashboard…
                  </p>
                  <button
                    className="sci-btn sci-btn--primary sci-btn--full"
                    style={{ marginTop: 24 }}
                    onClick={() => navigate("/dashboard")}
                  >
                    Go to Dashboard →
                  </button>
                </div>
              </div>
            </div>
          );
        }

        return (
          <div className="theme-scientific auth-page">
            <div className="auth-shell auth-shell--solo">
              <section className="auth-form-panel">
                <header className="auth-form-panel__header">
                  <div className="sci-eyebrow">ICMOBI / Sign in</div>
                  <h2 className="auth-form-panel__title">Welcome back</h2>
                  <p className="auth-form-panel__subtitle">
                    Enter your credentials to resume labeling.
                  </p>
                </header>

                {message && (
                  <div className="sci-alert sci-alert--danger">
                    <div className="sci-alert__icon" />
                    <span>{message}</span>
                  </div>
                )}

                {unverifiedEmail && (
                  <button
                    type="button"
                    className="sci-btn sci-btn--primary sci-btn--full"
                    style={{ marginBottom: 16 }}
                    onClick={() =>
                      navigate(
                        "/verify-email?email=" +
                          encodeURIComponent(unverifiedEmail)
                      )
                    }
                  >
                    Verify your email →
                  </button>
                )}

                <GoogleSignInButton
                  text="signin_with"
                  onCredential={(cred) => handleGoogle(googleLogin, cred)}
                  onError={() =>
                    setMessage("Couldn't start Google sign-in. Please try again.")
                  }
                />
                <div className="auth-divider">or sign in with email</div>

                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleSubmit(login);
                  }}
                >
                  <div className="sci-field">
                    <label className="sci-field__label sci-field__label--req" htmlFor="login-email">
                      Email
                    </label>
                    <input
                      id="login-email"
                      type="email"
                      className="sci-input"
                      placeholder="you@institution.edu"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      autoComplete="email"
                    />
                  </div>

                  <div className="sci-field">
                    <label className="sci-field__label sci-field__label--req" htmlFor="login-password">
                      Password
                    </label>
                    <input
                      id="login-password"
                      type="password"
                      className="sci-input"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete="current-password"
                    />
                  </div>

                  <button
                    type="submit"
                    className="sci-btn sci-btn--primary sci-btn--full"
                    disabled={isSubmitting}
                    style={{ marginTop: 8 }}
                  >
                    {isSubmitting ? "Authenticating…" : "Sign in →"}
                  </button>
                </form>

                <div className="auth-footer-link">
                  <span>Forgot your password?</span>
                  <a
                    href="/forgot-password"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate("/forgot-password");
                    }}
                  >
                    Reset it →
                  </a>
                </div>

                <div className="auth-footer-link">
                  <span>New to the lab?</span>
                  <a
                    href="/signup"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate("/signup");
                    }}
                  >
                    Request access →
                  </a>
                </div>
              </section>
            </div>
          </div>
        );
      }}
    </AuthConsumer>
  );
};

export default Login;
